import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { appointmentAPI } from '../services/api';
import Icon from '../components/Icon';
import OverflowMenu from '../components/OverflowMenu';
import { displayPatientCode } from '../utils/patientCode';

// Phone layout (max-width 768px) — see the "Navigator Patients / Appointments
// lists — phone layout" block in index.css. Everything tagged mlist-* is
// either restyled there or shown on only one side of the breakpoint
// (mlist-phone-only / mlist-desktop-only). The desktop table follows the Lab
// Tests list; its own rules are scoped to .appts-mlist in index.css.
const PHONE_QUERY = '(max-width: 768px)';

// The statuses an appointment actually has — there is no PENDING, so the old
// Pending chip always came up empty.
const FILTERS = ['ALL', 'SCHEDULED', 'CONFIRMED', 'ARRIVED', 'IN_CONSULTATION', 'COMPLETED', 'CANCELLED', 'NO_SHOW'];
const FILTER_LABELS = { ALL: 'All', NO_SHOW: 'No-show', IN_CONSULTATION: 'In consultation' };
const WHEN_LABELS = { today: 'Today', month: 'This month', upcoming: 'Upcoming' };

// Date-window filters for the dashboard's Today / This Month / Upcoming cards.
function inWindow(a, when) {
  const day = a.appointmentDate?.slice(0, 10) || '';
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  if (when === 'today') return day === today;
  if (when === 'month') return day.slice(0, 7) === today.slice(0, 7);
  if (when === 'upcoming') return ['SCHEDULED', 'CONFIRMED'].includes(a.status?.toUpperCase());
  return true;
}

const STATUS_COLORS = {
  SCHEDULED: 'badge-pending',
  CONFIRMED: 'badge-confirmed',
  ARRIVED: 'badge-pending',
  IN_CONSULTATION: 'badge-purple',
  COMPLETED: 'badge-active',
  CANCELLED: 'badge-inactive',
  NO_SHOW:   'badge-inactive',
};

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTHS   = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// Only picks the search placeholder now: the desktop one would clip on a phone.
function useIsPhone() {
  const [isPhone, setIsPhone] = useState(() => window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = (e) => setIsPhone(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isPhone;
}

// "2026-10-14" → "WED 14 OCT 2026". Parsed as a local date: new Date() on a
// bare ISO date is UTC midnight, which is the previous day west of Greenwich.
function dateHeading(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  if (!m) return 'NO DATE';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// "14:30:00" → "2:30 PM".
function time12(value) {
  const m = /^(\d{1,2}):(\d{2})/.exec(value || '');
  if (!m) return '';
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

const sentenceCase = (s = '') => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ');
const patientName  = (a) => a.patientName || [a.patient?.firstName, a.patient?.lastName].filter(Boolean).join(' ');
const initials     = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '—';

// Type and mode come either as enums (patient bookings: FOLLOW_UP, IN_PERSON)
// or as the free text the navigator's booking form sends ("Follow-up");
// enums are made readable, free text is shown as it is.
const ENUM_LABELS = { FOLLOW_UP: 'Follow-up', IN_PERSON: 'In-person' };
const enumLabel = (v) => (!v ? '—' : ENUM_LABELS[v] || (/^[A-Z][A-Z0-9_]*$/.test(v) ? sentenceCase(v) : v));

export default function AppointmentsPage() {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(true);
  // Dashboard cards deep-link here: ?status=SCHEDULED|CONFIRMED, ?when=today|month|upcoming.
  const [params] = useSearchParams();
  const [filter, setFilter]             = useState(FILTERS.includes(params.get('status')) ? params.get('status') : 'ALL');
  const [when, setWhen]                 = useState(Object.keys(WHEN_LABELS).includes(params.get('when')) ? params.get('when') : null);
  const [search, setSearch]             = useState('');
  const [query, setQuery]               = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // The search goes to the API too, ready for when the backend honours it;
  // until then the browser-side filter below is what narrows the list.
  useEffect(() => {
    let stale = false;
    setLoading(true);
    appointmentAPI.list({ page: 0, size: 50, ...(query && { search: query }) })
      .then(r => { if (!stale) setAppointments(Array.isArray(r.data) ? r.data : r.data?.content || []); })
      .catch(() => { if (!stale) setAppointments([]); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [query]);

  const activeFilter = filter;
  const needle = search.trim().toLowerCase();
  const filtered = appointments.filter(a =>
    (activeFilter === 'ALL' || a.status?.toUpperCase() === activeFilter)
    && (!when || inWindow(a, when))
    && (!needle || patientName(a).toLowerCase().includes(needle)));

  const cancelAppointment = async (a) => {
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await appointmentAPI.cancel(a.id);
      setAppointments(prev => prev.map(x => x.id === a.id ? {...x, status: 'CANCELLED'} : x));
    } catch { alert('Failed to cancel appointment.'); }
  };

  // Date headings (phone only) go in wherever the date changes, in the
  // server's own order — rows are never re-sorted.
  const rows = [];
  filtered.forEach((a, i) => {
    const day = a.appointmentDate?.slice(0, 10) || '';
    if (i === 0 || day !== (filtered[i - 1].appointmentDate?.slice(0, 10) || '')) {
      rows.push(
        <tr key={`day-${i}-${day}`} className="mlist-date-row mlist-phone-only">
          <td colSpan={7}>{dateHeading(day)}</td>
        </tr>
      );
    }
    const doctor = a.doctorName || a.doctor?.name;
    const status = a.status?.toUpperCase();
    const cancellable = ['CONFIRMED','PENDING'].includes(status);
    const name = patientName(a);
    const menuItems = [
      a.patientId && { label: 'View patient', onSelect: () => navigate(`/patients/${a.patientId}`) },
      cancellable && { label: 'Cancel appointment', danger: true, onSelect: () => cancelAppointment(a) },
    ].filter(Boolean);
    rows.push(
      <tr key={a.id} className="mlist-row">
        <td className="mlist-c-when">
          <strong className="mlist-desktop-only">{a.appointmentDate ? new Date(a.appointmentDate).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}</strong>
          <div className="mlist-desktop-only" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{time12(a.appointmentTime)}</div>
          <span className="mlist-time mlist-phone-only">{time12(a.appointmentTime)}</span>
        </td>
        <td className="mlist-c-main">
          <div className="patient-cell">
            <span className="avatar avatar--t2 mlist-desktop-only" aria-hidden="true">{initials(name)}</span>
            <div className="patient-cell__text">
              {/* On phones the link stretches over the whole row. */}
              <div className="mlist-name appts-name">
                {a.patientId
                  ? <Link className="mlist-row-link" to={`/patients/${a.patientId}`}>{a.patientName || a.patient?.firstName || '—'}</Link>
                  : (a.patientName || a.patient?.firstName || '—')}
              </div>
              <div className="appts-sub mlist-desktop-only">{displayPatientCode(a.patientCode)}</div>
            </div>
          </div>
        </td>
        <td className="mlist-c-who">
          <span className="mlist-desktop-only">{doctor || (a.navigatorName ? `${a.navigatorName} (Navigator)` : '—')}</span>
          <span className="mlist-phone-only">{doctor || a.navigatorName || ''}</span>
        </td>
        <td>{enumLabel(a.appointmentType || a.type)}</td>
        <td>{enumLabel(a.consultationMode || a.mode)}</td>
        <td className="mlist-c-status">
          <span className={`badge badge--dot ${STATUS_COLORS[status] || 'badge-pending'}`}>
            <span className="mlist-desktop-only">{(a.status || '').replace(/_/g, ' ')}</span>
            <span className="mlist-phone-only">{sentenceCase(a.status || '')}</span>
          </span>
        </td>
        <td className="mlist-c-end">
          <div className="appts-actions mlist-desktop-only">
            {a.patientId && (
              <button
                type="button"
                className="row-open"
                aria-label="Open patient"
                title="Open patient"
                onClick={() => navigate(`/patients/${a.patientId}`)}
              >
                <Icon name="arrowRight" size={16} />
              </button>
            )}
            {menuItems.length > 0 && (
              <OverflowMenu
                label={`More actions for ${name || 'this appointment'}`}
                items={menuItems}
              />
            )}
          </div>
          {cancellable && (
            <OverflowMenu
              className="mlist-phone-only"
              label={`More actions for ${name || 'this appointment'}`}
              items={[{ label: 'Cancel appointment', danger: true, onSelect: () => cancelAppointment(a) }]}
            />
          )}
        </td>
      </tr>
    );
  });

  return (
    <AppShell breadcrumbs={[{ label: 'Appointments' }]}>
      <div className="page-header mlist-header fade-in-up">
        <div className="mlist-header-text">
          <div className="page-title">Appointments</div>
          <div className="page-subtitle mlist-desktop-only">All scheduled appointments across patients</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary mlist-primary" onClick={() => navigate('/appointments/new')}>
            <span className="mlist-desktop-only btn-with-icon"><Icon name="plus" size={16} /> Book appointment</span>
            <span className="mlist-phone-only mlist-btn-label"><Icon name="plus" size={16} /> Book</span>
          </button>
        </div>
      </div>

      <div className="toolbar mlist-toolbar fade-in-up delay-2">
        <div className="search-input-wrap">
          <label className="mlist-sr-only" htmlFor="appointment-search">Search appointments</label>
          <span className="search-icon"><Icon name="search" size={15} /></span>
          <input
            id="appointment-search"
            className="search-input"
            placeholder={isPhone ? 'Search by patient name…' : 'Search loaded appointments by patient name…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="mlist-chips" role="group" aria-label="Filter by status">
          {FILTERS.map(s => (
            <button
              key={s}
              className={`btn btn-sm mlist-chip ${activeFilter === s ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={activeFilter === s}
              onClick={() => setFilter(s)}
            >
              {FILTER_LABELS[s] || s.charAt(0) + s.slice(1).toLowerCase()}
            </button>
          ))}
          {when && (
            <button className="btn btn-sm mlist-chip btn-primary" aria-label="Clear date filter"
              onClick={() => setWhen(null)}>
              {WHEN_LABELS[when]} ✕
            </button>
          )}
        </div>
      </div>

      <div className="table-wrap mlist-rows appts-mlist fade-in-up delay-3">
        <table className="appointments-table">
          <thead>
            <tr>
              <th>Date & Time</th><th>Patient</th><th>Doctor / Navigator</th><th>Type</th><th>Mode</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={7}><div className="loading-state"><div className="spinner" />Loading appointments…</div></td></tr>
              : filtered.length === 0
                ? <tr><td colSpan={7}><div className="empty-state"><div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="calendar" size={20} /></div><div className="empty-title">{needle ? 'No appointments match' : 'No appointments found'}</div><div className="empty-desc">{needle || activeFilter !== 'ALL' || when ? 'Try adjusting your search or filters' : 'Click Book appointment to schedule the first one'}</div></div></td></tr>
                : rows
            }
          </tbody>
        </table>
        <div className="table-footer mlist-footer">
          <span>{loading ? 'Loading…' : `Showing ${filtered.length} appointment${filtered.length !== 1 ? 's' : ''}`}</span>
        </div>
      </div>
    </AppShell>
  );
}
